import type { ReactNode } from 'react';
import { Button } from '../primitives/Button.tsx';
import { styleVars } from '../lib/style.ts';
import './form-panel.css';

export interface FormPanelProps {
  title?: ReactNode;
  note?: ReactNode;
  cancelLabel?: string;
  confirmLabel: string;
  confirmDisabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  children: ReactNode;
}

/**
 * Inline form panel from the design system.
 *
 * It opens in place, never as a modal, and pairs a quiet cancel action with the
 * affirmative primary action.
 */
export function FormPanel({
  title,
  note,
  cancelLabel = 'Cancel',
  confirmLabel,
  confirmDisabled = false,
  onCancel,
  onConfirm,
  children,
}: FormPanelProps) {
  return (
    <section className="formPanel">
      {title ? <h2 className="typeCardTitle">{title}</h2> : null}
      {children}
      <footer className="formPanelFooter" data-has-note={note ? 'true' : undefined}>
        {note ? <span className="formPanelNote">{note}</span> : null}
        <span className="formPanelActions">
          <Button variant="quiet" onClick={onCancel}>
            {cancelLabel}
          </Button>
          <Button variant="confirm" disabled={confirmDisabled} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </span>
      </footer>
    </section>
  );
}

export interface FormGridProps {
  children: ReactNode;
  columns?: string;
  min?: number;
  gap?: string;
}

/** Field grid with the design-system 170px default column floor. */
export function FormGrid({ children, columns, min = 170, gap }: FormGridProps) {
  return (
    <div
      className="formGrid"
      style={styleVars({
        '--form-grid-cols': columns ?? `repeat(auto-fit, minmax(${min}px, 1fr))`,
        '--form-grid-gap': gap,
      })}
    >
      {children}
    </div>
  );
}
